import {
  useQuery,
  QueryKey,
  useQueryClient,
  useMutation,
} from "@tanstack/react-query";

import {
  SonicFacilityData,
  SonicFacility,
} from "../common/classes/SonicFacility";

import { getFacilities } from "../services/api/facility/getFacilities";
import { postUpdateFacility } from "../services/api/facility/postUpdateFacility";
import { postFacilityPublicationApplication } from "../services/api/facility/postFacilityPublicationApplication";
import { getFacilitiesAdmin } from "../services/api/facility/getFacilitiesAdmin";
import { postUpdateFacilityAdmin } from "../services/api/facility/postUpdateFacilityAdmin";
import { postDeleteFacility } from "../services/api/facility/postDeleteFacility";
import { postUpdateFeaturedFacilities } from "../services/api/facility/postUpdateFeaturedFacilities";
import { getRecommendFacilities } from "../services/api/facility/getRecommendFacilities";
import { postFacilityApproval } from "../services/api/facility/postFacilityApproval";
import { SonicRelatedFacilityData } from "../common/classes/SonicRelatedFacility";

const initialData: {
  items: {
    facilities: SonicFacilityData[];
    related_facilities: SonicRelatedFacilityData[];
    totalEntries: number;
    page: number;
    totalPage: number;
  };
  isLoading: boolean;
  isError: boolean;
} = {
  items: {
    facilities: [],
    related_facilities: [],
    totalEntries: 0,
    page: 0,
    totalPage: 0,
  },
  isLoading: false,
  isError: false,
};

export const useGetFacility = (args: {
  id?: string | Array<string> | null;
  page?: number;
  search?: string;
  per?: number;
  sort_by?: string;
  desc?: boolean;
  prefecture?: string[];
  hostEvents?: string;
  foodService?: string;
  smoking?: string;
  authToken?: string | null;
}) => {
  const {
    id,
    page,
    search,
    per,
    sort_by,
    desc,
    prefecture,
    hostEvents,
    foodService,
    smoking,
    authToken,
  } = args;

  const facilityData = useQuery(["facility", args], ({ signal }) =>
    getFacilities(
      {
        id: id,
        page: page,
        per: per,
        search: search,
        sort_by: sort_by,
        desc: desc,
        prefecture: prefecture,
        hostEvents: hostEvents,
        foodService: foodService,
        smoking: smoking,
        authToken: authToken,
      },
      signal
    )
  );

  if (!facilityData.data)
    return { ...initialData, isLoading: facilityData.isLoading };

  const facilities: SonicFacilityData[] = [];
  facilityData.data.facilities.map((f) =>
    facilities.push(SonicFacility.serializeFacility(f))
  );

  const returnData: {
    facilities: SonicFacilityData[];
    related_facilities: SonicRelatedFacilityData[];
    totalEntries: number;
    page: number;
    totalPage: number;
  } = {
    facilities,
    related_facilities: facilityData.data.related_facilities,
    totalEntries: facilityData.data.total_entries,
    page: facilityData.data.page,
    totalPage: facilityData.data.total_pages,
  };

  return {
    items: returnData,
    isLoading: facilityData.isLoading,
    isError: facilityData.isError,
  };
};

export const useUpdateFacility = (key: QueryKey) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      authToken: string;
      update: { [key: string]: string | File };
    }) => {
      try {
        const res = await postUpdateFacility(args.authToken, args.update);

        return res !== null ? SonicFacility.serializeFacility(res) : null;
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: async () => queryClient.resetQueries(key),
    }
  );

  return {
    mutateAsync: mutation.mutateAsync,
    mutateLoading: mutation.isLoading,
  };
};

export const useRequestFacilityApproval = (key: QueryKey) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: { auth_token: string; id: string }) => {
      try {
        const res = await postFacilityPublicationApplication(
          args.auth_token,
          args.id
        );

        return res ? args.id : null;
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: async () => queryClient.resetQueries(key),
    }
  );

  return {
    requestAsync: mutation.mutateAsync,
    requestLoading: mutation.isLoading,
  };
};

export const useGetFacilityAdmin = (args: {
  authToken?: string | null;
  id?: string | null;
  status?: string;
  page?: number;
  search?: string;
  per?: number;
  sort_by?: string;
  desc?: boolean;
  owner?: string;
}) => {
  const { authToken, id, status, page, search, per, sort_by, desc, owner } =
    args;

  if (!authToken || id === null) return initialData;

  const facilityData = useQuery(["facilityAdmin", args], ({ signal }) =>
    getFacilitiesAdmin(
      {
        auth_token: authToken,
        id: id,
        status: status,
        page: page,
        per: per,
        search: search,
        sort_by: sort_by,
        desc: desc,
        owner: owner,
      },
      signal
    )
  );

  if (!facilityData.data)
    return { ...initialData, isLoading: facilityData.isLoading };

  const facilities: SonicFacilityData[] = [];
  facilityData.data.facilities.map((f) =>
    facilities.push(SonicFacility.serializeFacility(f))
  );

  const returnData: {
    facilities: SonicFacilityData[];
    totalEntries: number;
    page: number;
    totalPage: number;
  } = {
    facilities,
    totalEntries: facilityData.data.total_entries,
    page: facilityData.data.page,
    totalPage: facilityData.data.total_pages,
  };

  return {
    items: returnData,
    isLoading: facilityData.isLoading,
    isError: facilityData.isError,
  };
};

export const useUpdateFacilityAdmin = (key: QueryKey) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      authToken: string;
      update: { [key: string]: string | File };
    }) => {
      try {
        const res = await postUpdateFacilityAdmin(args.authToken, args.update);

        return res !== null ? SonicFacility.serializeFacility(res) : null;
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: async () => queryClient.resetQueries(key),
    }
  );

  return {
    mutateAsync: mutation.mutateAsync,
    mutateLoading: mutation.isLoading,
  };
};

export const useDeleteFacility = (key: QueryKey) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: { authToken: string; id: string }) => {
      try {
        return await postDeleteFacility(args.authToken, args.id);
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: async () => queryClient.invalidateQueries(key),
    }
  );

  return {
    mutateAsync: mutation.mutateAsync,
    mutateLoading: mutation.isLoading,
  };
};

export const useGetRecommendFacilities = () => {
  const initialData: { entries: SonicFacilityData[]; sentence: string } = {
    entries: [],
    sentence: "",
  };

  const res = useQuery(["recommendFacility"], ({ signal }) =>
    getRecommendFacilities(signal)
  );

  return {
    items: res.data ?? initialData,
    isLoading: res.isLoading,
    isError: res.isError,
  };
};

export const useUpdateFeaturedFacilities = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: { authToken: string; ids: string[]; sentence?: string }) => {
      try {
        return await postUpdateFeaturedFacilities(
          args.authToken,
          args.ids,
          args.sentence
        );
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: () => queryClient.resetQueries(["recommendFacility"]),
    }
  );

  return {
    mutateAsync: mutation.mutateAsync,
    mutateLoading: mutation.isLoading,
  };
};

export const useApproveFacility = (key: QueryKey) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      auth_token: string;
      id: string;
      message: string;
      approve: boolean;
    }) => {
      return await postFacilityApproval(
        args.auth_token,
        args.id,
        args.message,
        args.approve
      );
    },
    {
      onSuccess: async () => queryClient.resetQueries(key),
    }
  );

  return {
    mutateAsync: mutation.mutateAsync,
    mutateLoading: mutation.isLoading,
  };
};
